import React from 'react';
import { FileText, Download, Eye } from 'lucide-react';
import { docTypeBadge, fmtBytes } from './constants';

// One attachment list for a request's own files (docs/workflow-ui.md §4.3):
// icon · name · doc-type badge · size · View/Download. Engagement-master
// documents (current/superseded versions) stay with shared/EngagementAttachments.
//
//   files        — [{ id, originalName, sizeBytes, docType }]
//   downloadHref — (f) => url
//   badgeFor     — optional (f) => [label, cls] override (defaults to docType map)
//
// Reviewing an attachment should be a click, not a download.
// Images and PDFs open in a new tab (the server sends those inline — see
// server/utils/fileResponse.js, which also explains why only those two);
// .doc/.docx still download because no browser renders them. The label says
// which of the two will happen, so the button never lies.
const VIEWABLE = /\.(pdf|png|jpe?g|gif|webp)$/i;

const AttachmentsList = ({
  files = [],
  downloadHref,
  badgeFor = null,
  emptyText = 'No files attached.',
}) => {
  if (files.length === 0) {
    return <p className="text-sm text-slate-500">{emptyText}</p>;
  }
  return (
    <ul className="divide-y divide-slate-100">
      {files.map((f) => {
        const [label, cls] = badgeFor ? badgeFor(f) : docTypeBadge(f.docType);
        const viewable = VIEWABLE.test(f.originalName || '');
        return (
          <li key={f.id} className="flex items-center justify-between py-2 gap-2">
            <span className="flex items-center gap-2 text-sm text-slate-700 min-w-0">
              <FileText className="w-4 h-4 shrink-0 text-slate-400" />
              <span className="truncate">{f.originalName}</span>
              <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium shrink-0 ${cls}`}>
                {label}
              </span>
              {f.sizeBytes != null && <span className="text-xs text-slate-500 shrink-0">{fmtBytes(f.sizeBytes)}</span>}
            </span>
            <a
              href={downloadHref(f)}
              {...(viewable ? { target: '_blank', rel: 'noreferrer' } : {})}
              className="inline-flex items-center gap-1 text-sm text-blue-600 hover:text-blue-800 shrink-0"
            >
              {viewable
                ? <><Eye className="w-4 h-4" /> View</>
                : <><Download className="w-4 h-4" /> Download</>}
            </a>
          </li>
        );
      })}
    </ul>
  );
};

export default AttachmentsList;
